import { lazy, Suspense, useEffect, useState } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";

import { AuthContext, getAuthUser } from "helpers/auth";
import Spinner from "components/Spinner";
import PrivateRoute from "routes/PrivateRoute";
import { getCrypto, showError } from "helpers/utility";
import LayoutMaster from "components/LayoutMaster";
import CreateUser from "pages/Users/create";
import useAjax from "helpers/ajax";
import Roles from "pages/Roles";
import Permissions from "pages/Permissions";
import Companies from "pages/Companies";
import Licenses from "pages/Licenses";
import Devices from "pages/Devices";
import CreateCompany from "pages/Companies/create";
import CreateLicense from "pages/Licenses/create";
import CreateDevice from "pages/Devices/create";
import ImportDevice from "pages/Devices/import";
import ChangePassword from "pages/security/ChangePassword";
import RenewHisotry from "pages/Licenses/renewHistory";
import ForgotPassword from "pages/security/Forgot";
import ResetPassword from "pages/security/Reset";
import CreateRole from "pages/Roles/create";
import DeviceDetails from "pages/Devices/details";
import LicenseActivations from "pages/Reports/LicenseActivations";
import LicenseTransfers from "pages/Reports/LicenseTransfers";
import Coupons from "pages/Coupons";
import CreateCoupon from "pages/Coupons/create";
import CouponTransfers from "pages/Reports/CouponTransfers";

const Login = lazy(() => import("pages/security/Login"));
const Home = lazy(() => import("pages/Home"));
const Users = lazy(() => import("pages/Users"));
const NotFound = lazy(() => import("pages/NotFound"));

const App = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { get } = useAjax();
  const { encrypt, decrypt } = getCrypto();
  const [user, setUser] = useState(getAuthUser(decrypt));
  const [menuItems, setMenuItems] = useState([]);

  const onLogin = (data) => {
    const userData = {
      isAuthenticated: true,
      ...data,
    };
    // Encrypt user's details and store in localstorage
    const encText = encrypt(userData);
    localStorage.setItem("ctx", encText);

    setUser(userData);
    if (location.state?.from) {
      navigate(location.state.from);
    } else {
      navigate("/");
    }
  };

  const onLogout = () => {
    localStorage.clear();
    //setUser({ isAuthenticated: false });
    navigate("/account/login", {});
  };

  useEffect(() => {
    if (user.isAuthenticated) {
      get("api/v1/users/getrolemenu")
        .then((resp) => {
          setMenuItems(resp);
        })
        .catch((err) => {
          showError(err);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <AuthContext.Provider value={{ logout: onLogout, login: onLogin, user }}>
      <Suspense fallback={<Spinner />}>
        <Routes>
          <Route exact path="account/login" element={<Login />} />
          <Route exact path="account/forgot" element={<ForgotPassword />} />
          <Route exact path="account/reset" element={<ResetPassword />} />

          <Route element={<LayoutMaster menuItems={menuItems} />}>
            <Route
              exact
              path="/"
              element={
                <PrivateRoute>
                  <Home />
                </PrivateRoute>
              }
            />
            <Route path="security/users">
              <Route
                index
                element={
                  <PrivateRoute scopes={["users.list"]}>
                    <Users />
                  </PrivateRoute>
                }
              />
              <Route
                path="create"
                element={
                  <PrivateRoute scopes={["users.create"]}>
                    <CreateUser />
                  </PrivateRoute>
                }
              />
              <Route
                path="edit/:id"
                element={
                  <PrivateRoute scopes={["users.edit"]}>
                    <CreateUser />
                  </PrivateRoute>
                }
              />
            </Route>
            <Route path="security/roles">
              <Route
                index
                element={
                  <PrivateRoute scopes={["roles.list"]}>
                    <Roles />
                  </PrivateRoute>
                }
              />
              <Route
                path="create"
                element={
                  <PrivateRoute scopes={["roles.create"]}>
                    <CreateRole />
                  </PrivateRoute>
                }
              />
              <Route
                path="edit/:id"
                element={
                  <PrivateRoute scopes={["roles.edit"]}>
                    <CreateRole />
                  </PrivateRoute>
                }
              />
              <Route
                path="permissions/:roleid"
                element={
                  <PrivateRoute scopes={["roles.permissions"]}>
                    <Permissions />
                  </PrivateRoute>
                }
              />
            </Route>
            <Route
              path="security/changepassword"
              element={
                <PrivateRoute>
                  <ChangePassword />
                </PrivateRoute>
              }
            />
            <Route path="admin/companies">
              <Route
                index
                element={
                  <PrivateRoute scopes={["companies.list"]}>
                    <Companies />
                  </PrivateRoute>
                }
              />
              <Route
                path="create"
                element={
                  <PrivateRoute scopes={["companies.create"]}>
                    <CreateCompany />
                  </PrivateRoute>
                }
              />
              <Route
                path="edit/:id"
                element={
                  <PrivateRoute scopes={["companies.edit"]}>
                    <CreateCompany />
                  </PrivateRoute>
                }
              />
            </Route>

            <Route path="devices/licenses">
              <Route
                index
                element={
                  <PrivateRoute scopes={["licenses.list"]}>
                    <Licenses />
                  </PrivateRoute>
                }
              />
              <Route
                path="create"
                element={
                  <PrivateRoute scopes={["licenses.create"]}>
                    <CreateLicense />
                  </PrivateRoute>
                }
              />
              <Route
                path="history"
                element={
                  <PrivateRoute scopes={["licenses.renew"]}>
                    <RenewHisotry />
                  </PrivateRoute>
                }
              />
            </Route>

            <Route path="devices/devices">
              <Route
                index
                element={
                  <PrivateRoute scopes={["devices.list"]}>
                    <Devices />
                  </PrivateRoute>
                }
              />
              <Route
                path="create"
                element={
                  <PrivateRoute scopes={["devices.create"]}>
                    <CreateDevice />
                  </PrivateRoute>
                }
              />
              <Route
                path="edit/:id"
                element={
                  <PrivateRoute scopes={["devices.edit"]}>
                    <CreateDevice />
                  </PrivateRoute>
                }
              />
              <Route
                path="import"
                element={
                  <PrivateRoute scopes={["devices.import"]}>
                    <ImportDevice />
                  </PrivateRoute>
                }
              />
              <Route
                path="details"
                element={
                  <PrivateRoute>
                    <DeviceDetails />
                  </PrivateRoute>
                }
              />
            </Route>

            <Route path="devices/coupons">
              <Route
                index
                element={
                  <PrivateRoute scopes={["coupons.list"]}>
                    <Coupons />
                  </PrivateRoute>
                }
              />
              <Route
                path="create"
                element={
                  <PrivateRoute scopes={["coupons.create"]}>
                    <CreateCoupon />
                  </PrivateRoute>
                }
              />
            </Route>

            <Route path="reports">
              <Route
                path="licenseactivations"
                element={
                  <PrivateRoute scopes={["reports.licenseactivations"]}>
                    <LicenseActivations />
                  </PrivateRoute>
                }
              />
              <Route
                path="licensetransfers"
                element={
                  <PrivateRoute scopes={["reports.licensetransfers"]}>
                    <LicenseTransfers />
                  </PrivateRoute>
                }
              />
              <Route
                path="coupontransfers"
                element={
                  <PrivateRoute scopes={["reports.coupontransfers"]}>
                    <CouponTransfers />
                  </PrivateRoute>
                }
              />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </Suspense>
    </AuthContext.Provider>
  );
};

export default App;
